import { generateClient } from 'aws-amplify/api';
import { getCurrentUser } from 'aws-amplify/auth';

const ROLE_NAMES = {
  'devsecops': 'DevSecOps Engineer',
  'devex': 'Developer Experience Engineer',
  'cloud-platform-engineer': 'Cloud Platform Engineer',
  'devops-questionnaire': 'DevOps Engineer',
  'sre-questionnaire': 'Site Reliability Engineer',
  'penetration-testing': 'Penetration Tester',
  'cybergrc': 'Cyber GRC Analyst',
  'product-manager': 'Product Manager',
  'ai-data-engineer': 'AI Data Engineer',
  'security-analyst': 'Security Analyst',
  'full-stack-engineer': 'Full Stack Engineer',
  'devsecai': 'DevSecAI Engineer',
};

// Server acknowledgement is required; never clear unsynchronised browser work.
export async function saveRoadmapToPortal({ roadmap, roleKey, title, subtitle, totalMonths }) {
  try {
    const user = await getCurrentUser();
    const client = generateClient();
    const items = (roadmap.items || []).map((item, idx) => ({
      id: item.id || `item-${idx}`, title: item.title || item.name || '',
      duration: item.duration || 0, month: item.month || item.startMonth || 1,
      startMonth: item.startMonth || item.month || 1,
      endMonth: item.endMonth || item.month || item.startMonth || 1,
      cost: item.cost ?? null, description: item.description || '',
      type: item.type || 'skill', coveredInBootcamp: item.coveredInBootcamp || false,
    }));
    const certifications = items.filter(item => item.type === 'certification').map(item => ({
      id: item.id, name: item.title, cost: item.cost || 0, duration: item.duration,
    }));
    const run = {roleKey: roleKey || 'unknown', roleName: ROLE_NAMES[roleKey] || roleKey || 'Technology',
      title: title || `My ${ROLE_NAMES[roleKey] || roleKey || 'Technology'} Roadmap`,
      subtitle: subtitle || '', totalMonths: totalMonths || roadmap.totalMonths || 6};
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(JSON.stringify({items,run})));
    const fingerprint = Array.from(new Uint8Array(digest)).map(x => x.toString(16).padStart(2,'0')).join('');
    const id = `${user.userId}-roadmap-${fingerprint}`;
    async function query(query, variables, field) {
      const response = await client.graphql({query, variables, authMode: 'userPool'});
      if (response.errors?.length) throw new Error('The server could not save your roadmap. Please try again.');
      return response.data?.[field];
    }
    const existing = await query('query($id:ID!){getRoadmapRun(id:$id){id _deleted}}',{id},'getRoadmapRun');
    if (existing && !existing._deleted) return {success:true,roadmapRun:existing};
    const saved = await query('query($id:ID!){getRoadmap(id:$id){id _deleted}}',{id},'getRoadmap');
    if (!saved) {
      const created = await query('mutation($input:CreateRoadmapInput!){createRoadmap(input:$input){id}}',
        {input:{id,items,certifications}},'createRoadmap');
      if (!created?.id) throw new Error('Roadmap not saved. Please try again.');
    } else if (saved._deleted || existing?._deleted) {
      throw new Error('This roadmap was previously removed. Change your plan before saving a new copy.');
    }
    const roadmapRun = await query('mutation($input:CreateRoadmapRunInput!){createRoadmapRun(input:$input){id title totalMonths}}',
      {input:{id,...run,roadmapRunRoadmapId:id}},'createRoadmapRun');
    if (!roadmapRun?.id) throw new Error('Your roadmap is stored but its portal entry is not ready. Retry Save to finish it.');
    return {success:true,roadmapRun};
  } catch (error) {
    return {success:false,error:error.message || 'Roadmap not saved. Please sign in and try again.'};
  }
}
export async function isUserAuthenticated() {
  try { await getCurrentUser(); return true; } catch { return false; }
}

export async function loadSavedRoadmaps() {
  await getCurrentUser();
  const client = generateClient();
  const runs = [];
  let nextToken = null;
  do {
    const result = await client.graphql({
      query: `query SavedRoadmaps($nextToken:String){listRoadmapRuns(limit:50,nextToken:$nextToken){
        items {id roleKey roleName title subtitle totalMonths createdAt _version _deleted
          roadmap {id _version items{id title duration month startMonth endMonth cost description type coveredInBootcamp}
            certifications{id name cost duration}}} nextToken}}`,
      variables:{nextToken},authMode:'userPool',
    });
    if (result.errors?.length) throw new Error('Saved roadmaps could not be loaded. Please retry.');
    const page = result.data?.listRoadmapRuns;
    if (!page) throw new Error('Saved roadmaps could not be loaded. Please retry.');
    runs.push(...(page.items || []).filter(run => run && !run._deleted));
    nextToken=page.nextToken;
  } while(nextToken);
  return runs;
}
